import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BlogModal = ({ isOpen, onClose, blogId = null }) => {
    const [formData, setFormData] = useState({
        id: '',
        title: '',
        category: '',
        image: '',
        description: '',
        content: ''
    });

    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            if (blogId) {
                const fetchBlogPost = async () => {
                    setLoading(true);
                    setError(null);
                    try {
                        const response = await axios.get(`${process.env.REACT_APP_API_URL}/blog/${blogId}`);
                        setFormData({
                            id: response.data.id || '',
                            title: response.data.title || '',
                            category: response.data.category || '',
                            image: response.data.image || '',
                            description: response.data.description || '',
                            content: response.data.content || ''
                        });
                        setImageFile(null);
                    } catch (err) {
                        console.error('Error fetching blog post data:', err);
                        setError('Failed to load blog post data.');
                    } finally {
                        setLoading(false);
                    }
                };
                fetchBlogPost();
            } else {
                setFormData({
                    id: '',
                    title: '',
                    category: '',
                    image: '',
                    description: '',
                    content: ''
                });
                setImageFile(null);
            }
        }
    }, [isOpen, blogId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const form = new FormData();
            form.append('title', formData.title);
            form.append('category', formData.category);
            form.append('description', formData.description);
            form.append('content', formData.content);

            if (!blogId) {
                form.append('id', formData.id); // only send id for new blog
            }

            if (imageFile) {
                form.append('image', imageFile);
            } else if (formData.image) {
                form.append('image', formData.image);
            }

            let response;
            if (blogId) {
                response = await axios.put(`${process.env.REACT_APP_API_URL}/blog/${blogId}`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                response = await axios.post(`${process.env.REACT_APP_API_URL}/blog`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            console.log('Blog post saved:', response.data);
            onClose();
        } catch (err) {
            console.error('Save failed:', err.response?.data || err.message);
            setError(err.response?.data?.message || 'Failed to save blog post.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 *:font-sans text-left">
            <div className="fixed inset-0 bg-black opacity-50"></div>
            <div className="bg-white p-8 rounded-lg shadow-lg z-50 w-[90vw] sm:w-[80vw] max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex mb-3 justify-between items-center">
                    <div>
                        <div className="font-semibold text-xl">
                            {blogId ? 'Edit Blog Post' : 'Add New Blog Post'}
                        </div>
                        {blogId && (
                            <div className="text-sm text-gray-500 mt-1">Post ID: {formData.id}</div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-black focus:outline-none text-3xl">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {loading && <p className="text-center text-gray-500">Saving...</p>}
                {error && <p className="text-center text-red-500">{error}</p>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* ID (only editable when creating) */}
                    <div>
                        <label htmlFor="id" className="block text-sm mb-1 capitalize text-gray-700">Post ID</label>
                        <input
                            type="text"
                            name="id"
                            id="id"
                            value={formData.id}
                            onChange={handleChange}
                            className="border px-3 py-2 rounded-lg text-sm w-full"
                            disabled={!!blogId}
                            required={!blogId}
                        />
                        {blogId && <p className="text-xs text-gray-500 mt-1">This ID cannot be changed.</p>}
                    </div>

                    {/* Title */}
                    <div>
                        <label htmlFor="title" className="block text-sm mb-1 capitalize text-gray-700">Title</label>
                        <input
                            type="text"
                            name="title"
                            id="title"
                            value={formData.title}
                            onChange={handleChange}
                            className="border px-3 py-2 rounded-lg text-sm w-full"
                            required
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label htmlFor="category" className="block text-sm mb-1 capitalize text-gray-700">Category</label>
                        <input
                            type="text"
                            name="category"
                            id="category"
                            value={formData.category}
                            onChange={handleChange}
                            className="border px-3 py-2 rounded-lg text-sm w-full"
                            required
                        />
                    </div>

                    {/* Image Upload */}
                    <div>
                        <label htmlFor="imageFile" className="block text-sm mb-1 text-gray-700">Upload New Image</label>
                        <input
                            type="file"
                            name="imageFile"
                            id="imageFile"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="border px-3 py-2 rounded-lg text-sm w-full"
                        />
                        {formData.image && !imageFile && (
                            <div className="mt-2 text-sm text-gray-600">
                                Current image: <a href={formData.image} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{formData.image.length > 50 ? formData.image.substring(0, 50) + '...' : formData.image}</a>
                                <img src={formData.image} alt="Current Preview" className="h-24 w-auto mt-2 object-contain rounded border border-gray-200" />
                            </div>
                        )}
                        {imageFile && (
                            <div className="mt-2 text-sm text-gray-600">
                                New image selected: <span className="font-semibold">{imageFile.name}</span>
                                <img src={URL.createObjectURL(imageFile)} alt="New Preview" className="h-24 w-auto mt-2 object-contain rounded border border-gray-200" />
                            </div>
                        )}
                    </div>

                    {/* Short Description */}
                    <div>
                        <label htmlFor="description" className="block text-sm mb-1 text-gray-700">Short Description</label>
                        <textarea
                            name="description"
                            id="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            className="border px-3 py-2 rounded-lg text-sm w-full h-[10vh]"
                            required
                        />
                    </div>

                    {/* Full Content */}
                    <div>
                        <label htmlFor="content" className="block text-sm mb-1 text-gray-700">Full Content</label>
                        <textarea
                            name="content"
                            id="content"
                            value={formData.content}
                            onChange={handleChange}
                            rows="8"
                            className="border px-3 py-2 rounded-lg text-sm w-full h-[20vh]"
                            required
                        />
                    </div>

                    <div className="text-center mt-6">
                        <button
                            type="submit"
                            className="bg-[#173d73] text-white px-6 py-2 rounded-lg text-sm duration-300 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (blogId ? 'Save Changes' : 'Add Blog Post')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BlogModal;
